import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import axios from "axios";

import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";

const UNMAPPED = "__none__";

const buildInitialMapping = (contactSchema = {}, columns = []) => {
  const colNames = columns.map((c) => c.name);
  return Object.keys(contactSchema).reduce((acc, contactKey) => {
    acc[contactKey] = colNames.includes(contactKey) ? contactKey : UNMAPPED;
    return acc;
  }, {});
};

const MapSheetSchema = () => {
  const { data, isLoading, isError, error } = useApiEp(
    `/user/get-schema-to-map`
  );

  const [mapping, setMapping] = useState({});
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  useEffect(() => {
    if (data?.contactSchema && data?.sheetSchema?.columns) {
      setMapping(
        buildInitialMapping(data.contactSchema, data.sheetSchema.columns)
      );
    }
  }, [data]);

  const contactKeys = data ? Object.keys(data.contactSchema) : [];
  const sheetColumns = data?.sheetSchema?.columns ?? [];
  const requiredCols = new Set(
    sheetColumns.filter((c) => c.required).map((c) => c.name)
  );

  const buildPayload = () =>
    Object.entries(mapping).reduce((acc, [contactKey, sheetCol]) => {
      if (sheetCol !== UNMAPPED) acc[contactKey] = sheetCol;
      return acc;
    }, {});

  const handleDelete = (contactKey) =>
    setMapping((prev) => ({ ...prev, [contactKey]: UNMAPPED }));

  const handleSave = async () => {
    const payload = buildPayload();
    try {
      setSaving(true);
      await axios.post(
        `${API_ENDPOINT}/user/save-schema-mapping`,
        {
          sheetId: data.sheetSchema._id,
          mappedSchema: payload,
        },
        getHeader()
      );
      setSnack({
        open: true,
        msg: "Mapping saved successfully!",
        severity: "success",
      });
    } catch (err) {
      setSnack({
        open: true,
        msg: err?.response?.data?.message ?? "Failed to save mapping.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 3, mb: 6 }}>
      <BackBtn />

      <Typography variant="h5" fontWeight={700} mt={2} mb={0.5}>
        Map Contact → Sheet Schema
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Sheet: <strong>{data?.sheetSchema?.sheetName}</strong>
        {data?.sheetSchema?.customerId && (
          <> &nbsp;·&nbsp; {data.sheetSchema.customerId}</>
        )}
      </Typography>

      {/* loading */}
      {isLoading && (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={64} />
          ))}
        </Stack>
      )}

      {/* error */}
      {isError && (
        <Alert severity="error">
          {error?.message ?? "Failed to load schema."}
        </Alert>
      )}

      {/* mapping rows */}
      {!isLoading && !isError && data && (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
          {/* header */}
          <Box
            display="grid"
            gridTemplateColumns="1fr 56px 1fr 40px"
            alignItems="center"
            px={3}
            py={1.5}
            bgcolor="grey.100"
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              CONTACT FIELD
            </Typography>
            <span />
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.secondary"
            >
              SHEET COLUMN
            </Typography>
            <span />
          </Box>

          <Divider />

          {contactKeys.map((contactKey, idx) => {
            const contactValue = data.contactSchema[contactKey];
            const selectedCol = mapping[contactKey] ?? UNMAPPED;
            const isMapped = selectedCol !== UNMAPPED;

            return (
              <React.Fragment key={contactKey}>
                {idx > 0 && <Divider />}
                <Box
                  display="grid"
                  gridTemplateColumns="1fr 56px 1fr 40px"
                  alignItems="center"
                  px={3}
                  py={2}
                  sx={{
                    transition: "background .15s",
                    "&:hover": { bgcolor: "grey.50" },
                  }}
                >
                  {/* contact field */}
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {contactKey}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {String(contactValue)}
                    </Typography>
                  </Box>

                  {/* arrow */}
                  <Box display="flex" justifyContent="center">
                    <SwapHorizRoundedIcon
                      fontSize="small"
                      sx={{
                        color: isMapped ? "primary.main" : "text.disabled",
                      }}
                    />
                  </Box>

                  {/* sheet column selector */}
                  <Select
                    size="small"
                    value={selectedCol}
                    onChange={(e) =>
                      setMapping((prev) => ({
                        ...prev,
                        [contactKey]: e.target.value,
                      }))
                    }
                    displayEmpty
                    sx={{ minWidth: 160 }}
                    renderValue={(val) =>
                      val === UNMAPPED ? (
                        <Typography variant="body2" color="text.disabled">
                          — not mapped —
                        </Typography>
                      ) : (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2">{val}</Typography>
                          {requiredCols.has(val) && (
                            <Chip
                              label="required"
                              size="small"
                              color="warning"
                            />
                          )}
                        </Stack>
                      )
                    }
                  >
                    <MenuItem value={UNMAPPED}>
                      <Typography variant="body2" color="text.secondary">
                        — not mapped —
                      </Typography>
                    </MenuItem>
                    {sheetColumns.map((col) => (
                      <MenuItem key={col.name} value={col.name}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span>{col.name}</span>
                          {col.required && (
                            <Chip
                              label="required"
                              size="small"
                              color="warning"
                            />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {col.type}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>

                  {/* delete button */}
                  <Box display="flex" justifyContent="center">
                    <IconButton
                      size="small"
                      disabled={!isMapped}
                      onClick={() => handleDelete(contactKey)}
                      sx={{
                        color: "error.main",
                        opacity: isMapped ? 1 : 0,
                        transition: "opacity .15s",
                        "&:hover": { bgcolor: "error.50" },
                      }}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Paper>
      )}

      {/* payload preview */}
      {!isLoading && !isError && data && (
        <Paper
          variant="outlined"
          sx={{ mt: 3, p: 2, borderRadius: 2, bgcolor: "grey.50" }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            mb={1}
            display="block"
          >
            PAYLOAD PREVIEW
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{ m: 0, fontFamily: "monospace" }}
          >
            {JSON.stringify(buildPayload(), null, 2)}
          </Typography>
        </Paper>
      )}

      {/* save button */}
      {!isLoading && !isError && data && (
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            size="large"
            disabled={saving}
            onClick={handleSave}
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <CheckCircleOutlineRoundedIcon />
              )
            }
          >
            {saving ? "Saving…" : "Save Mapping"}
          </Button>
        </Box>
      )}

      {/* snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MapSheetSchema;
